import isUndefined from 'lodash/isUndefined';
import type { SingleValue } from 'react-select';

import { Nullable } from 'chaika-frontend-types/frontend-cms';

import { useCheckOnFocus } from '@hooks/useCheckOnFocus';
import { normalizeYoString } from '@utils/normalizeYoString';

import { resolvePlaceholder } from '../../utils/selectHelpers';
import type { OptionType } from '../../Select';

interface Params {
    defaultOptions: OptionType<string>[] | undefined;
    value: Nullable<OptionType> | undefined;
    selectedValue: Nullable<string | number> | undefined;
    placeholder: string | undefined;
    title: string | undefined;
    name: string | undefined;
    isFilteredOnServer: boolean;
    createLabelValue?(value: string): string;
    loadOptions(value: string, name?: string): Promise<OptionType<string>[]>;
    onChange(params: Nullable<OptionType>, name?: string): void;
}

interface Data {
    value: Nullable<OptionType>;
    placeholder: string | undefined;
    handleChange(selectedItem: SingleValue<OptionType>): void;
    handleCreateOption(value: string): void;
    handleLoadOptions(value: string): Promise<OptionType<string>[]>;
    handleFocus(): void;
    handleBlur(): void;
}

export function useAsyncCreatableSelect({
    defaultOptions,
    value,
    selectedValue,
    placeholder,
    title,
    name,
    isFilteredOnServer,
    createLabelValue,
    loadOptions,
    onChange,
}: Params): Data {
    const { isOnFocus, handleFocus, handleBlur } = useCheckOnFocus();

    const resolvedValue = !isUndefined(selectedValue) ? makeValue(defaultOptions, selectedValue) : (value ?? null);

    return {
        value: resolvedValue,
        placeholder: resolvePlaceholder({ placeholder, title, isOnFocus }),
        handleChange: selectedItem => onChange(selectedItem, name),
        handleCreateOption: createValue => {
            const creatableValue = createLabelValue?.(createValue) ?? createValue;
            const option: OptionType<string> = { value: creatableValue, label: creatableValue };

            defaultOptions?.unshift(option);
            onChange(option, name);
        },
        handleLoadOptions: query => loadOptions(isFilteredOnServer ? normalizeYoString(query) : query, name),
        handleFocus,
        handleBlur,
    };
}

function makeValue(
    defaultOptions: OptionType<string>[] | undefined,
    selectedValue: Nullable<string | number>,
): Nullable<OptionType> {
    if (selectedValue === null || selectedValue === undefined || selectedValue === '') {
        return null;
    }

    return (
        defaultOptions?.find(option => option.value === selectedValue || option.label === selectedValue) ?? {
            value: String(selectedValue),
            label: String(selectedValue),
        }
    );
}
